import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Archive, Trash2, Save, Wrench, User, Car, WalletCards, PencilLine, LockKeyhole, X, Clock3 } from "lucide-react";
import { VehicleForm } from "@/pages/Vehicles";
import { ColorChip } from "@/components/ColorChip";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("de-DE") : "—";
const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
const fmtDuration = (seconds) => {
  const totalMinutes = Math.floor(Number(seconds || 0) / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (!hours) return `${minutes} Min.`;
  return minutes ? `${hours} Std. ${minutes} Min.` : `${hours} Std.`;
};

function VehicleSectionHeading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-vehicle-section-heading">
      <span className="stellar-vehicle-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

const DETAIL_LABELS = {
  fuel_type: { benzin: "Benzin", diesel: "Diesel", elektro: "Elektro", hybrid: "Hybrid", lpg: "LPG/CNG", other: "Sonstige" },
  body_type: { limousine: "Limousine", kombi: "Kombi", suv: "SUV", coupe: "Coupé", cabrio: "Cabrio", van: "Van/Transporter", pickup: "Pickup", sonstige: "Sonstige" },
};

function VehicleDataRow({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/50 py-2.5 last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className={`text-base text-right font-semibold break-words ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

const STATUS_STYLE = {
  angelegt: "border-amber-200 bg-amber-50 text-amber-700",
  in_bearbeitung: "border-orange-200 bg-orange-50 text-orange-700",
  fertig: "border-emerald-200 bg-emerald-50 text-emerald-700",
  active: "border-emerald-200 bg-emerald-50 text-emerald-700",
  archived: "border-slate-200 bg-slate-100 text-slate-600",
};

const STATUS_LABEL = {
  angelegt: "Auftrag angelegt",
  in_bearbeitung: "In Bearbeitung",
  fertig: "Letzter Auftrag fertig",
  active: "Aktiv",
  archived: "Archiviert",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [lightboxId, setLightboxId] = useState(null);
  const [correctionMode, setCorrectionMode] = useState(false);

  const { data: vehicle, isLoading } = useQuery({
    queryKey: ["vehicle", id],
    queryFn: async () => (await api.get(`/vehicles/${id}`)).data,
  });

  const { data: appointments = [] } = useQuery({
    queryKey: ["vehicle-appointments", id],
    queryFn: async () => (await api.get(`/vehicles/${id}/appointments`)).data,
  });

  const { data: jobs = [] } = useQuery({
    queryKey: ["vehicle-jobs", id],
    queryFn: async () => (await api.get(`/vehicles/${id}/jobs`)).data,
  });

  const { data: payments = [] } = useQuery({
    queryKey: ["vehicle-payments", id],
    queryFn: async () => (await api.get("/payments", { params: { vehicle_id: id } })).data,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers"],
    queryFn: async () => (await api.get("/customers")).data,
  });

  useEffect(() => {
    if (vehicle) {
      setForm({
        customer_id: vehicle.customer_id || "",
        manufacturer: vehicle.manufacturer || "",
        model: vehicle.model || "",
        year: vehicle.year ?? "",
        license_plate: vehicle.license_plate || "",
        vin: vehicle.vin || "",
        color: vehicle.color || "",
        mileage: vehicle.mileage ?? "",
        fuel_type: vehicle.fuel_type || "",
        body_type: vehicle.body_type || "",
        notes: vehicle.notes || "",
        engine: vehicle.engine || "",
        internal_reference: vehicle.internal_reference || "",
        registration_date: vehicle.registration_date || "",
      });
    }
  }, [vehicle]);

  const saveMut = useMutation({
    mutationFn: async () => (await api.put(`/vehicles/${id}`, {
      ...form,
      year: form.year ? Number(form.year) : null,
      mileage: form.mileage ? Number(form.mileage) : null,
      registration_date: form.registration_date || null,
    }, { params: { correction: jobs.length > 0 } })).data,
    onSuccess: () => {
      toast.success(correctionMode ? "Stammdaten korrigiert" : "Gespeichert");
      setCorrectionMode(false);
      qc.invalidateQueries({ queryKey: ["vehicle", id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const archiveMut = useMutation({
    mutationFn: async () => (await api.delete(`/vehicles/${id}`)).data,
    onSuccess: () => { toast.success("Fahrzeug archiviert"); qc.invalidateQueries({ queryKey: ["vehicles"] }); navigate("/fahrzeuge"); },
    onError: (e) => toast.error(apiError(e)),
  });

  const hardDeleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/vehicles/${id}?hard=true`)).data,
    onSuccess: () => { toast.success("Fahrzeug gelöscht"); qc.invalidateQueries({ queryKey: ["vehicles"] }); navigate("/fahrzeuge"); },
    onError: (e) => toast.error(apiError(e)),
  });

  if (isLoading || !form || !vehicle) return <div className="p-8 text-muted-foreground">Laden…</div>;

  // Photos aggregated: legacy from Appointments + new from Jobs (decision D backward-compat)
  const legacyPhotos = appointments.flatMap((a) => (a.photos || []).map((p) => ({ ...p, _galleryId: `appointment-${a.id}-${p.id}`, source: "appointment", source_id: a.id, source_label: a.service_name })));
  const jobPhotos = jobs.flatMap((j) => (j.photos || []).map((p) => ({ ...p, _galleryId: `job-${j.id}-${p.id}`, source: "job", source_id: j.id, source_label: j.title })));
  const allPhotos = [...jobPhotos, ...legacyPhotos];
  const imagePhotos = allPhotos.filter((p) => p.content_type?.startsWith("image/")).slice(0, 24);

  const now = new Date();
  const upcoming = appointments.find((a) => new Date(a.start) >= now);
  const completedJobs = jobs.filter((j) => j.status === "fertig");
  const hasJobHistory = jobs.length > 0;
  const currentJob = jobs.find((j) => j.status === "in_bearbeitung")
    || jobs.find((j) => j.status === "angelegt");
  const latestCompletedJob = completedJobs
    .slice()
    .sort((a, b) => new Date(b.completed_at || b.updated_at) - new Date(a.completed_at || a.updated_at))[0];
  const derivedStatus = vehicle.status === "archived"
    ? "archived"
    : currentJob?.status || (latestCompletedJob ? "fertig" : "active");
  const totalWorkSeconds = jobs.reduce((sum, job) => sum + Number(job.total_work_seconds || 0), 0);
  const visitDates = jobs
    .map((job) => job.completed_at || job.created_at)
    .filter(Boolean)
    .map((date) => new Date(date))
    .filter((date) => !Number.isNaN(date.getTime()))
    .sort((a, b) => a - b);
  const lastVisit = visitDates.at(-1);
  const validPayments = payments.filter((payment) => payment.status !== "storniert");
  const paidTotal = validPayments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0);
  const paidByJob = validPayments.reduce((map, payment) => {
    if (payment.job_id) map[payment.job_id] = (map[payment.job_id] || 0) + Number(payment.amount || 0);
    return map;
  }, {});

  return (
    <div className="stellar-page stellar-detail-page">
      <Link to="/fahrzeuge" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-vehicles">
        <ArrowLeft className="h-4 w-4" /> Fahrzeuge
      </Link>

      {/* Identity — single source of truth for color: one ColorChip below the title. */}
      <div className="stellar-detail-heading mb-6 flex-wrap">
        <div className="stellar-icon-bubble stellar-icon-violet"><Car /></div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-xl sm:text-3xl font-bold tracking-tight break-words">
            {[vehicle.manufacturer, vehicle.model].filter(Boolean).join(" ") || "Fahrzeug"}
            {vehicle.year && <span className="text-muted-foreground"> · Bj. {vehicle.year}</span>}
          </h1>
          {vehicle.color && (
            <div className="mt-1">
              <ColorChip value={vehicle.color} size="md" testId="vehicle-color-chip" />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-muted-foreground">
            {vehicle.customer_name && (
              <Link to={`/kunden/${vehicle.customer_id}`} className="inline-flex items-center gap-1 hover:text-foreground" data-testid="vehicle-customer-link">
                <User className="h-3.5 w-3.5" /> {vehicle.customer_name}
              </Link>
            )}
            {vehicle.license_plate && <span className="font-mono">· {vehicle.license_plate}</span>}
            {vehicle.vin && <span className="font-mono text-xs">· VIN {vehicle.vin}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div
            className={`inline-flex min-h-10 items-center rounded-full border px-4 text-sm font-medium ${STATUS_STYLE[derivedStatus] || STATUS_STYLE.active}`}
            data-testid="vehicle-derived-status"
            title="Automatisch aus dem aktuellen Auftrag ermittelt"
          >
            {STATUS_LABEL[derivedStatus] || derivedStatus}
          </div>
        </div>
      </div>

      <section className="stellar-vehicle-section">
        <VehicleSectionHeading number="1" icon={Car} title="Fahrzeugübersicht" />
        <div className="grid gap-4 lg:grid-cols-2">
        <div className={`premium-card p-5 ${!hasJobHistory || correctionMode ? "lg:col-span-2" : ""}`}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="font-display font-semibold flex items-center gap-2"><div className="stellar-section-icon stellar-section-violet"><Car /></div>Fahrzeugakte</h2>
            {hasJobHistory && !correctionMode && (
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold text-slate-600">
                <LockKeyhole className="h-3 w-3" /> Geschützt
              </span>
            )}
          </div>

          {!hasJobHistory || correctionMode ? (
            <>
              {correctionMode && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                  Nur tatsächliche Fehler in den Fahrzeug-Stammdaten korrigieren. Abgeschlossene Aufträge und Rechnungen bleiben unverändert.
                </div>
              )}
              <VehicleForm
                form={form}
                setForm={setForm}
                customers={customers}
                readOnlyOwner={{ id: vehicle.customer_id, name: vehicle.customer_name }}
              />
            </>
          ) : (
            <div data-testid="vehicle-readonly-dossier">
              <VehicleDataRow
                label="Kunde"
                value={vehicle.customer_id ? <Link to={`/kunden/${vehicle.customer_id}`} className="hover:text-primary hover:underline">{vehicle.customer_name}</Link> : vehicle.customer_name}
              />
              <VehicleDataRow label="Hersteller" value={vehicle.manufacturer} />
              <VehicleDataRow label="Modell" value={vehicle.model} />
              <VehicleDataRow label="Karosserie" value={DETAIL_LABELS.body_type[vehicle.body_type] || vehicle.body_type} />
              <VehicleDataRow label="Baujahr" value={vehicle.year} />
              <VehicleDataRow label="Farbe" value={vehicle.color ? <ColorChip value={vehicle.color} size="sm" /> : ""} />
            </div>
          )}
        </div>

        {hasJobHistory && !correctionMode && (
          <div className="premium-card p-5" data-testid="vehicle-quick-overview">
            <div className="mb-4 flex items-center gap-2">
              <div className="stellar-section-icon stellar-section-green"><WalletCards /></div>
              <h2 className="font-display font-semibold">Auf einen Blick</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Status</div>
                <div className="mt-1 font-display text-base font-semibold">{STATUS_LABEL[derivedStatus] || derivedStatus}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Kennzeichen</div>
                <div className="mt-1 font-mono text-base font-semibold">{vehicle.license_plate || "—"}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Aufträge</div>
                <div className="mt-1 font-display text-xl font-semibold">{jobs.length}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground">Letzter Besuch</div>
                <div className="mt-1 font-display text-base font-semibold">{lastVisit ? fmtDate(lastVisit) : "—"}</div>
              </div>
              <div className="rounded-xl bg-orange-50/70 p-3">
                <div className="text-xs text-muted-foreground">Arbeitszeit</div>
                <div className="mt-1 font-display text-base font-semibold text-orange-700">{fmtDuration(totalWorkSeconds)}</div>
              </div>
              <div className="rounded-xl bg-emerald-50/70 p-3">
                <div className="text-xs text-muted-foreground">Bezahlt</div>
                <div className="mt-1 font-display text-base font-semibold text-emerald-700">{fmtEur(paidTotal)}</div>
              </div>
            </div>
          </div>
        )}
        </div>
      </section>

      <section className="stellar-vehicle-section">
        <VehicleSectionHeading number="2" icon={Wrench} title="Aktueller Auftrag" />
        {currentJob ? (
          <Link to={`/auftraege/${currentJob.id}`} className="premium-card stellar-current-job-card flex items-center gap-4 p-4 hover:bg-slate-50" data-testid="vehicle-current-job">
            <div className="stellar-section-icon stellar-section-orange"><Wrench /></div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold truncate">{currentJob.title || "Auftrag"}</div>
              <div className="text-xs text-muted-foreground mt-1 truncate">
                {(currentJob.services || []).map((service) => service.service_name).filter(Boolean).join(", ") || "Keine Leistungen"}
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="font-semibold">{fmtEur(currentJob.total_price)}</div>
              <div className="text-xs text-orange-600">{STATUS_LABEL[currentJob.status] || currentJob.status}</div>
            </div>
          </Link>
        ) : (
          <div className="premium-card p-5 text-sm text-muted-foreground">Kein aktiver Auftrag.</div>
        )}
      </section>

      <section className="stellar-vehicle-section">
        <VehicleSectionHeading number="3" icon={Clock3} title="Termine & Arbeiten" />
        <div className="space-y-4">
          {upcoming && (
            <div className="premium-card border-blue-200 bg-blue-50/60 p-4">
              <div className="text-xs uppercase tracking-widest text-blue-700 mb-1">Nächster Termin</div>
              <Link to={`/kalender?open=${upcoming.id}`} className="block hover:underline">
                <div className="font-medium">{upcoming.service_name}</div>
                <div className="text-sm text-muted-foreground">{fmtDT(upcoming.start)}</div>
              </Link>
            </div>
          )}

          <div className="premium-card overflow-hidden">
            <div className="p-4 border-b border-border/60 flex items-center gap-2">
              <div className="stellar-section-icon stellar-section-green"><Wrench /></div>
              <h2 className="font-display font-semibold">Auftragsverlauf ({jobs.length})</h2>
            </div>
            <div className="divide-y divide-border/60">
              {jobs.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">Noch keine Aufträge.</div>}
              {jobs.map((job) => {
                const paid = paidByJob[job.id] || 0;
                const open = Math.max(0, Number(job.total_price || 0) - paid);
                const isCompleted = job.status === "fertig";
                return (
                <Link key={job.id} to={`/auftraege/${job.id}`} className="flex items-center gap-3 p-4 hover:bg-muted/40" data-testid={`vehicle-job-${job.id}`}>
                  <div className={`w-1 self-stretch rounded-full ${isCompleted ? "bg-emerald-500" : job.status === "in_bearbeitung" ? "bg-orange-500" : "bg-amber-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{job.title || "Auftrag"}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDT(job.completed_at || job.created_at)} · {STATUS_LABEL[job.status] || job.status.replace("_", " ")}
                      {(job.services || []).length > 0 && ` · ${job.services.map((service) => service.service_name).join(", ")}`}
                    </div>
                  </div>
                  <div className="text-right text-xs shrink-0">
                    <div className="font-medium">{fmtEur(job.total_price)}</div>
                    <div className={open <= 0.005 ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}>
                      <WalletCards className="inline h-3 w-3 mr-1" />
                      {open <= 0.005 ? "Bezahlt" : paid > 0 ? `${fmtEur(open)} offen` : "Unbezahlt"}
                    </div>
                  </div>
                </Link>
              )})}
            </div>
          </div>

          {allPhotos.length > 0 && (
            <div className="premium-card overflow-hidden">
              <div className="p-4 border-b border-border/60 font-display font-semibold">Fotos ({allPhotos.length})</div>
              <div className="grid grid-cols-3 md:grid-cols-4 gap-2 p-4">
                {imagePhotos.map((p) => (
                  <button type="button" key={p._galleryId} onClick={() => setLightboxId(p._galleryId)} className="block cursor-zoom-in" data-testid={`vehicle-photo-${p.id}`} title={`${p.source === "job" ? "Auftrag" : "Termin"}: ${p.source_label || ""}`}>
                    <img src={fileUrl(p.storage_path)} alt={p.filename || ""} className="w-full h-24 object-cover rounded-md border transition-transform hover:scale-[1.02]" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="stellar-vehicle-section">
        <VehicleSectionHeading number="4" icon={PencilLine} title="Aktionen" />
        <div className="premium-card stellar-vehicle-actions flex flex-wrap gap-3 p-4">
          {(!hasJobHistory || correctionMode) && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="save-vehicle-detail">
              <Save className="h-4 w-4 mr-1" /> {correctionMode ? "Korrektur speichern" : "Speichern"}
            </Button>
          )}
          {correctionMode && (
            <Button variant="outline" onClick={() => { setCorrectionMode(false); setForm({ ...vehicle }); }} data-testid="cancel-vehicle-correction"><X className="h-4 w-4 mr-1" /> Abbrechen</Button>
          )}
          {hasJobHistory && !correctionMode && (
            <Button variant="outline" onClick={() => { if (confirm("Stammdaten wirklich korrigieren? Diese Funktion ist nur für Eingabefehler gedacht.")) setCorrectionMode(true); }} data-testid="correct-vehicle-master-data">
              <PencilLine className="h-4 w-4 mr-1" /> Stammdaten korrigieren
            </Button>
          )}
          {vehicle.status !== "archived" && (
            <Button variant="outline" onClick={() => { if (confirm("Fahrzeug archivieren?")) archiveMut.mutate(); }} data-testid="archive-vehicle"><Archive className="h-4 w-4 mr-1" /> Archivieren</Button>
          )}
          {!hasJobHistory && (
            <Button variant="ghost" className="text-destructive ml-auto" onClick={() => { if (confirm("Fahrzeug UNWIDERRUFLICH löschen?")) hardDeleteMut.mutate(); }} data-testid="delete-vehicle"><Trash2 className="h-4 w-4 mr-1" /> Löschen</Button>
          )}
        </div>
      </section>
      <PhotoLightbox photos={imagePhotos} activeId={lightboxId} onClose={() => setLightboxId(null)} />
    </div>
  );
}
